import React from 'react';
import PropTypes from 'prop-types';

import {Avatar, Box, Stack, Typography, FiveStar} from 'views/components';
import {PersonOutlined} from 'views/icons';
import {Translate} from 'views/module';

import moment from 'moment';

import VerifiedLabel from '../components/VerifiedIcon';

const ReviewSection = (props) => {
  const {questions: {firstName}, reviews} = props;

  const formattedReviews = React.useMemo(() => reviews.map((review) => ({
    ...review,
    text: (review.text).trim(),
  })), [reviews]);

  const avgRating = (reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length).toFixed(1);

  const avatarColors = ['#A7F3D0', '#FDE68A', '#BFDBFE', '#FCA5A5', '#C7D2FE', '#F9A8D4'];

  if (!reviews.length) {
    return null;
  }

  return (
    <Stack alignItems="flex-start" width="100%" spacing={2} sx={{}}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h5" fontWeight={700}>
            <Translate expression='caregiverProfile.Review.Title'/>
          </Typography>
          <FiveStar
            value={Number(avgRating)}
            precision={0.5}
            readOnly
            size="medium"
            sx={{
              color: 'primary.main',
            }}
          />
          <Typography variant = "subtitle1" fontWeight={700}>
            {avgRating}
          </Typography>
          <Typography variant="subtitle1" color="grey.600">
            <Translate
              expression={reviews.length == 1 ? 'caregiverProfile.Review.NumberOfReview' : 'caregiverProfile.Review.NumberOfReviews'}
              substitute={{numReviews: reviews.length}}
            />
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{}}>
          <Translate
            expression='caregiverProfile.Review.Subtitle'
            substitute={{caregiverName: firstName}}
          />
        </Typography>
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {xs: '1fr', sm: '1fr 1fr'},
          gap: 2,
          width: '100%',
        }}
      >
        {
          formattedReviews.map((r) => {
            const avatarColor = avatarColors[r.reviewerName.length % avatarColors.length];

            return (
              <Stack
                key={r.caregiverReviewRn}
                spacing={1}
                sx={{
                  'p': 2,
                  'borderRadius': 4,
                  'bgcolor': 'white',
                  'width': '100%',
                  'boxSizing': 'border-box',
                  'border': '1px solid',
                  'borderColor': 'grey.300',
                  'm': 0,
                  'transition': 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  <Avatar sx={{bgcolor: avatarColor}}>
                    {
                        r.reviewerName.length > 0 ?
                        r.reviewerName
                            .split(' ')
                            .map((n) => n[0])
                            .join('')
                            .slice(0, 2) :
                        <PersonOutlined />
                    }
                  </Avatar>
                  <Stack spacing={0.25} alignContent="flex-start">
                    <Typography variant="subtitle2" fontWeight="bold">
                      {r.reviewerName}
                    </Typography>
                    <Stack direction="row" alignContent="center" spacing={0.5}>
                      <FiveStar value={r.rating} precision={0.5} max={5} size="small" readOnly/>
                      <Typography variant="body2" color="grey.700">
                        {moment(r.date).format('MMMM YYYY')}
                      </Typography>
                    </Stack>

                  </Stack>
                  <Box sx={{flex: 1}} />
                  {
                    r.isVerified && <VerifiedLabel/>
                  }
                </Stack>
                <Box
                  sx={{
                    textAlign: 'center',
                    bgcolor: 'color.white',
                    borderRadius: 2,
                    border: 1,
                    borderWidth: 1.5,
                    borderColor: 'grey.300',
                    px: 1,
                    width: 'fit-content',
                  }}
                >
                  <Typography variant="caption" fontWeight="bold" color="color.black">
                    {r.relationship}
                  </Typography>
                </Box>
                <Typography variant="body2" color="color.black">
                  {r.text}
                </Typography>
              </Stack>
            );
          })
        }
      </Box>
    </Stack>
  );
};

ReviewSection.propTypes = {
  questions: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
  }).isRequired,
  reviews: PropTypes.arrayOf(
      PropTypes.shape({
        caregiverReviewRn: PropTypes.string.isRequired,
        reviewerName: PropTypes.string.isRequired,
        relationship: PropTypes.string.isRequired,
        date: PropTypes.string.isRequired,
        text: PropTypes.string.isRequired,
        isVerified: PropTypes.bool.isRequired,
        rating: PropTypes.number.isRequired,
      }),
  ).isRequired,
};

export default React.memo(ReviewSection);
